import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, forkJoin } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { SubHeaderService } from '../../store/subheader/subheader.service';
import { ReportingService } from '../../store/reporting/reporting.service';
import {
  Period,
  SalesPerformanceFacetDimension,
  SalesPerformanceFacetItem,
  SalesPerformanceFacetItemWithFlag,
  UNASSIGNED_FACET_ID,
} from '../../store/reporting/types';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReportingApiService } from '../../store/reporting/reporting.api.services';
import { TimeZoneService } from '../../../common/time-zone/time-zone.service';
import { toNaiveISO } from '../../helper/datehelper';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dashboard-subheader',
  templateUrl: './dashboard-subheader.component.html',
  styleUrls: ['./dashboard-subheader.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class DashboardSubheaderComponent implements OnInit, OnChanges {
  @Input() startDate!: Date;
  @Output() startDateChange = new EventEmitter<Date>();

  @Input() endDate!: Date;
  @Output() endDateChange = new EventEmitter<Date>();

  // Two-way bound so the parent can restore a previously selected period (e.g.
  // after a drill-down round-trip). Defaults to CurrentMonth when the parent
  // does not bind it.
  @Input() period: string = 'CurrentMonth';
  @Output() periodChange = new EventEmitter<string>();

  periodLabel: string;
  periods: Period[];
  defaultPeriod: string = 'CurrentMonth';

  periods$: Observable<Period[]>;

  private readonly productCategoryListSubject = new BehaviorSubject<
    SalesPerformanceFacetItemWithFlag[]
  >([]);
  private readonly sourcesListSubject = new BehaviorSubject<
    SalesPerformanceFacetItemWithFlag[]
  >([]);
  private readonly mediumsListSubject = new BehaviorSubject<
    SalesPerformanceFacetItemWithFlag[]
  >([]);
  private readonly campaignsListSubject = new BehaviorSubject<
    SalesPerformanceFacetItemWithFlag[]
  >([]);

  productCategoryListItems$ = this.productCategoryListSubject.asObservable();
  sourcesListItem$ = this.sourcesListSubject.asObservable();
  mediumsListItem$ = this.mediumsListSubject.asObservable();
  campaignsListItem$ = this.campaignsListSubject.asObservable();

  @Input() productCategoryIds: number[] = [];
  @Output() productCategoryIdsChange = new EventEmitter<number[]>();

  @Input() campaignIds: number[] = [];
  @Output() campaignIdsChange = new EventEmitter<number[]>();

  @Input() sourceIds: number[] = [];
  @Output() sourceIdsChange = new EventEmitter<number[]>();

  @Input() mediumIds: number[] = [];
  @Output() mediumIdsChange = new EventEmitter<number[]>();

  //"All" Value
  @Input() productCategoryIdAllValue: number = -1; //id
  @Input() campaignIdAllValue: number = -1; //id
  @Input() sourceIdAllValue: number = -1; //id
  @Input() mediumIdAllValue: number = -1; //id

  private readonly refresh$ = new Subject<void>();

  // Keeps the last-seen name for each id per dimension so that a previously
  // selected value can still be rendered (greyed out) after it falls out of
  // the cascaded facet result.
  private readonly nameCache: Record<
    SalesPerformanceFacetDimension,
    Map<number, string>
  > = {
    ProductCategory: new Map(),
    Source: new Map(),
    Medium: new Map(),
    Campaign: new Map(),
    SalesPerson: new Map(),
    SalesTeam: new Map(),
  };

  constructor(
    private readonly subHeaderService: SubHeaderService,
    private readonly reportingService: ReportingService,
    private readonly reportingApiService: ReportingApiService,
    private readonly timeZoneService: TimeZoneService,
    private readonly translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    // The subheader owns the "All" sentinel, so it materializes its own
    // default: an empty selection from the parent means "All". This runs
    // before the inner <ng-select> reads the model, so the dropdowns render
    // the "All" option checked, and the normalized value is emitted back so
    // the parent stays in sync.
    this.initSelectionDefaults();

    this.periods$ = this.reportingService.getPeriods.value$;
    this.reportingService.getPeriods.call(new Date());

    this.refresh$
      .pipe(debounceTime(0), untilDestroyed(this))
      .subscribe(() => this.refetchAllFacets());

    this.periods$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((periods) => {
        this.periods = periods;
        //init (uses the parent-restored period, defaults to CurrentMonth)
        this.onPeriodChange(this.period);
      });
  }

  // Falls back to "All" only when the parent left a dimension empty; a
  // non-empty selection from the parent is always preserved (parent has the
  // final say). The normalised value is emitted so the two-way binding and
  // the parent's own API calls stay aligned with what the dropdown displays.
  private initSelectionDefaults(): void {
    if (!this.productCategoryIds?.length) {
      this.productCategoryIds = [this.productCategoryIdAllValue];
      this.productCategoryIdsChange.emit(this.productCategoryIds);
    }
    if (!this.sourceIds?.length) {
      this.sourceIds = [this.sourceIdAllValue];
      this.sourceIdsChange.emit(this.sourceIds);
    }
    if (!this.mediumIds?.length) {
      this.mediumIds = [this.mediumIdAllValue];
      this.mediumIdsChange.emit(this.mediumIds);
    }
    if (!this.campaignIds?.length) {
      this.campaignIds = [this.campaignIdAllValue];
      this.campaignIdsChange.emit(this.campaignIds);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const watched = [
      'startDate',
      'endDate',
      'productCategoryIds',
      'campaignIds',
      'sourceIds',
      'mediumIds',
    ];
    if (watched.some((k) => changes[k])) {
      this.refresh$.next();
    }
  }

  triggerAction(actionName) {
    this.subHeaderService.setTriggerAction(actionName);
  }

  onPeriodChange(newValue: string) {
    this.period = newValue;
    this.periodChange.emit(newValue);
    this.periods.forEach((period) => {
      if (period.value == newValue) {
        this.startDate = new Date(period.startDate);
        this.startDateChange.emit(this.startDate);

        this.endDate = new Date(period.endDate);
        this.endDateChange.emit(this.endDate);
      }
    });
    this.periodLabel = formatPeriodLabel(
      new Date(this.startDate),
      new Date(this.endDate),
    );
    this.refresh$.next();
  }

  onProductCategoryIdsChange() {
    this.buildProductCategoryIds();

    this.productCategoryIdsChange.emit(this.productCategoryIds);
    this.refresh$.next();
  }

  private buildProductCategoryIds() {
    const lastElement = this.productCategoryIds.slice(-1)[0];
    if (lastElement === this.productCategoryIdAllValue) {
      this.productCategoryIds = [this.productCategoryIdAllValue];
      return;
    }

    //then remove the "All" Value if in []
    const indexMinus1 = this.productCategoryIds.findIndex(
      (value) => value === this.productCategoryIdAllValue,
    );
    if (indexMinus1 > -1) {
      this.productCategoryIds.splice(indexMinus1, 1);
      this.productCategoryIds = [...this.productCategoryIds]; // refresh
    }

    if (this.productCategoryIds.length == 0) {
      this.productCategoryIds = [this.productCategoryIdAllValue];
      return;
    }
    return;
  }

  onCampaignIdsChange() {
    this.buildCampaignIds();

    this.campaignIdsChange.emit(this.campaignIds);
    this.refresh$.next();
  }

  private buildCampaignIds() {
    const lastElement = this.campaignIds.slice(-1)[0];
    if (lastElement === this.campaignIdAllValue) {
      this.campaignIds = [this.campaignIdAllValue];
      return;
    }

    //then remove the "All" Value if in []
    const indexMinus1 = this.campaignIds.findIndex(
      (value) => value === this.campaignIdAllValue,
    );
    if (indexMinus1 > -1) {
      this.campaignIds.splice(indexMinus1, 1);
      this.campaignIds = [...this.campaignIds]; // refresh
    }

    if (this.campaignIds.length == 0) {
      this.campaignIds = [this.campaignIdAllValue];
      return;
    }
    return;
  }

  onSourceIdsChange() {
    this.buildSourceIds();

    this.sourceIdsChange.emit(this.sourceIds);
    this.refresh$.next();
  }

  private buildSourceIds() {
    const lastElement = this.sourceIds.slice(-1)[0];
    if (lastElement === this.sourceIdAllValue) {
      this.sourceIds = [this.sourceIdAllValue];
      return;
    }
    //then remove the "All" Value if in []
    const indexMinus1 = this.sourceIds.findIndex(
      (value) => value === this.sourceIdAllValue,
    );
    if (indexMinus1 > -1) {
      this.sourceIds.splice(indexMinus1, 1);
      this.sourceIds = [...this.sourceIds]; // refresh
    }

    if (this.sourceIds.length == 0) {
      this.sourceIds = [this.sourceIdAllValue];
      return;
    }
    return;
  }

  onMediumIdsChange() {
    this.buildMediumIds();

    this.mediumIdsChange.emit(this.mediumIds);
    this.refresh$.next();
  }

  private buildMediumIds() {
    const lastElement = this.mediumIds.slice(-1)[0];
    if (lastElement === this.mediumIdAllValue) {
      this.mediumIds = [this.mediumIdAllValue];
      return;
    }

    //then remove the "All" Value if in []
    const indexMinus1 = this.mediumIds.findIndex(
      (value) => value === this.mediumIdAllValue,
    );
    if (indexMinus1 > -1) {
      this.mediumIds.splice(indexMinus1, 1);
      this.mediumIds = [...this.mediumIds]; // refresh
    }

    if (this.mediumIds.length == 0) {
      this.mediumIds = [this.mediumIdAllValue];
      return;
    }
    return;
  }

  private refetchAllFacets(): void {
    if (!this.startDate || !this.endDate) return;

    forkJoin({
      ProductCategory: this.reportingApiService.getSalesPerformanceFacetItems({
        dimension: 'ProductCategory',
        filter: this.buildFacetFilter('ProductCategory'),
      }),
      Source: this.reportingApiService.getSalesPerformanceFacetItems({
        dimension: 'Source',
        filter: this.buildFacetFilter('Source'),
      }),
      Medium: this.reportingApiService.getSalesPerformanceFacetItems({
        dimension: 'Medium',
        filter: this.buildFacetFilter('Medium'),
      }),
      Campaign: this.reportingApiService.getSalesPerformanceFacetItems({
        dimension: 'Campaign',
        filter: this.buildFacetFilter('Campaign'),
      }),
    })
      .pipe(untilDestroyed(this))
      .subscribe(({ ProductCategory, Source, Medium, Campaign }) => {
        this.productCategoryListSubject.next(
          this.buildFacetList(
            'ProductCategory',
            this.normalizeUnassigned(ProductCategory),
            this.productCategoryIds,
            this.productCategoryIdAllValue,
            'DASHBOARD_LBL_PRODUCT_CATEGORY_ALL',
          ),
        );
        this.sourcesListSubject.next(
          this.buildFacetList(
            'Source',
            this.normalizeUnassigned(Source),
            this.sourceIds,
            this.sourceIdAllValue,
            'DASHBOARD_LBL_SOURCE_ALL',
          ),
        );
        this.mediumsListSubject.next(
          this.buildFacetList(
            'Medium',
            this.normalizeUnassigned(Medium),
            this.mediumIds,
            this.mediumIdAllValue,
            'DASHBOARD_LBL_MEDIUM_ALL',
          ),
        );
        this.campaignsListSubject.next(
          this.buildFacetList(
            'Campaign',
            this.normalizeUnassigned(Campaign),
            this.campaignIds,
            this.campaignIdAllValue,
            'DASHBOARD_LBL_CAMPAIGN_ALL',
          ),
        );
      });
  }

  // ng-select can't bind null, so the API's null "unassigned" id is swapped for
  // the UI sentinel here, at the component boundary. It travels back to the API
  // as null in appendClause; -2 never leaves the front.
  private normalizeUnassigned(
    apiItems: SalesPerformanceFacetItem[],
  ): SalesPerformanceFacetItemWithFlag[] {
    return apiItems.map((item) => ({
      ...item,
      id: item.id ?? UNASSIGNED_FACET_ID,
    }));
  }

  private buildFacetFilter(
    excludeDimension: SalesPerformanceFacetDimension,
  ): string {
    const clauses: string[] = [];

    const endDatePlusOne: Date = new Date(this.endDate);
    endDatePlusOne.setDate(endDatePlusOne.getDate() + 1);

    clauses.push(
      `creationDate>=${this.timeZoneService.fromLocalToUtc(
        toNaiveISO(this.startDate),
        false,
      )}`,
    );
    clauses.push(
      `CreationDate<${this.timeZoneService.fromLocalToUtc(
        toNaiveISO(endDatePlusOne),
        false,
      )}`,
    );

    if (excludeDimension !== 'ProductCategory') {
      this.appendClause(
        clauses,
        'ProductCategoryId',
        this.productCategoryIds,
        this.productCategoryIdAllValue,
      );
    }
    if (excludeDimension !== 'Campaign') {
      this.appendClause(
        clauses,
        'CampaignId',
        this.campaignIds,
        this.campaignIdAllValue,
      );
    }
    if (excludeDimension !== 'Source') {
      this.appendClause(
        clauses,
        'SourceId',
        this.sourceIds,
        this.sourceIdAllValue,
      );
    }
    if (excludeDimension !== 'Medium') {
      this.appendClause(
        clauses,
        'MediumId',
        this.mediumIds,
        this.mediumIdAllValue,
      );
    }

    return clauses.join(',');
  }

  private appendClause(
    clauses: string[],
    field: string,
    ids: number[],
    allValue: number,
  ): void {
    if (!ids || ids.length === 0 || ids.includes(allValue)) return;
    // The "unassigned" bucket is the UI sentinel -2 internally; it travels to
    // the API as null (Gridify IS NULL). -2 never leaves the front.
    clauses.push(
      `(${ids
        .map((id) =>
          id === UNASSIGNED_FACET_ID ? `${field}=null` : `${field}=${id}`,
        )
        .join('|')})`,
    );
  }

  private buildFacetList(
    dimension: SalesPerformanceFacetDimension,
    apiItems: SalesPerformanceFacetItemWithFlag[],
    selectedIds: number[],
    allValue: number,
    allLabelKey: string,
  ): SalesPerformanceFacetItemWithFlag[] {
    const merged = this.mergeWithSelected(
      dimension,
      apiItems,
      selectedIds,
      allValue,
    );
    const totalCount = apiItems.reduce((sum, i) => sum + i.count, 0);
    const allOption: SalesPerformanceFacetItemWithFlag = {
      id: allValue,
      name: this.translateService.instant(allLabelKey),
      count: totalCount,
    };
    return [allOption, ...merged];
  }

  private mergeWithSelected(
    dimension: SalesPerformanceFacetDimension,
    apiItems: SalesPerformanceFacetItemWithFlag[],
    selectedIds: number[],
    allValue: number,
  ): SalesPerformanceFacetItemWithFlag[] {
    const cache = this.nameCache[dimension];
    apiItems.forEach((item) => cache.set(item.id, item.name));

    const selected = (selectedIds ?? []).filter((id) => id !== allValue);
    const apiIds = new Set(apiItems.map((i) => i.id));

    const visible: SalesPerformanceFacetItemWithFlag[] = apiItems
      .filter((item) => item.count > 0 || selected.includes(item.id))
      .sort((a, b) => b.count - a.count);

    const missing: SalesPerformanceFacetItemWithFlag[] = selected
      .filter((id) => !apiIds.has(id))
      .map((id) => ({
        id,
        name: cache.get(id) ?? '',
        count: 0,
        disabled: true,
      }));

    return [...visible, ...missing];
  }
}

function formatPeriodLabel(startDate: Date, endDate: Date) {
  const fullOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const monthOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  };
  const dayOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    day: 'numeric',
  };
  let periodLabel: string = '';
  if (startDate.getFullYear() == endDate.getFullYear()) {
    periodLabel =
      'Du ' +
      startDate.toLocaleDateString('fr-FR', monthOptions) +
      ' au ' +
      endDate.toLocaleDateString('fr-FR', fullOptions);

    if (startDate.getMonth() == endDate.getMonth()) {
      periodLabel =
        'Du ' +
        startDate.toLocaleDateString('fr-FR', dayOptions) +
        ' au ' +
        endDate.toLocaleDateString('fr-FR', fullOptions);

      if (startDate.getDate() == endDate.getDate()) {
        periodLabel =
          'Le ' + startDate.toLocaleDateString('fr-FR', fullOptions);
      }
    }
  }

  if (startDate.getFullYear() != endDate.getFullYear()) {
    periodLabel =
      'Du ' +
      startDate.toLocaleDateString('fr-FR', fullOptions) +
      ' au ' +
      endDate.toLocaleDateString('fr-FR', fullOptions);
  }
  return periodLabel;
}
